import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from 'react-native';

interface Appointment {
  id: string;
  customerName: string;
  phone: string;
  email: string;
  address: string;
  serviceType: string;
  hairLength: string;
  hairType: string;
  previousColor: string;
  specialInstructions: string;
  appointmentDate: string;
  appointmentTime: string;
  status: string;
  workerId?: string;
  workerName?: string;
  actualDuration?: number;
}

interface DurationTrackingModalProps {
  visible: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onSave: (appointmentId: string, duration: number) => void;
}

const DurationTrackingModal: React.FC<DurationTrackingModalProps> = ({
  visible,
  onClose,
  appointment,
  onSave,
}) => {
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isTracking && startTime) {
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTracking, startTime]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startTracking = () => {
    const now = new Date();
    setStartTime(now);
    setEndTime(null);
    setIsTracking(true);
    setElapsedTime(0);
  };

  const stopTracking = () => {
    const now = new Date();
    setEndTime(now);
    setIsTracking(false);
  };

  const saveDuration = () => {
    if (!appointment) return;

    const duration = Math.floor(elapsedTime / 60); // Convert to minutes
    if (duration < 1) {
      Alert.alert('Error', 'Duration must be at least 1 minute');
      return;
    }

    onSave(appointment.id, duration);
    Alert.alert('Success', `Duration saved: ${duration} minutes`);
  };

  const resetTracking = () => {
    setStartTime(null);
    setEndTime(null);
    setIsTracking(false);
    setElapsedTime(0);
  };

  if (!appointment) return null;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet">
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Track Duration</Text>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          {/* Appointment Info */}
          <View style={styles.appointmentInfo}>
            <Text style={styles.customerName}>{appointment.customerName}</Text>
            <Text style={styles.appointmentDetails}>
              📅 {appointment.appointmentDate} at {appointment.appointmentTime}
            </Text>
            <Text style={styles.appointmentDetails}>
              💇‍♀️ {appointment.serviceType}
            </Text>
            <Text style={styles.appointmentDetails}>
              🎨 {appointment.specialInstructions}
            </Text>
            {appointment.workerName && (
              <Text style={styles.appointmentDetails}>
                👤 Worker: {appointment.workerName}
              </Text>
            )}
          </View>

          {/* Timer Display */}
          <View style={styles.timerContainer}>
            <Text style={styles.timerLabel}>Elapsed Time</Text>
            <Text style={styles.timerDisplay}>
              {formatTime(elapsedTime)}
            </Text>
          </View>

          {/* Tracking Controls */}
          <View style={styles.controlsContainer}>
            {!isTracking && !startTime && (
              <TouchableOpacity style={styles.startButton} onPress={startTracking}>
                <Text style={styles.startButtonText}>Start Tracking</Text>
              </TouchableOpacity>
            )}

            {isTracking && (
              <TouchableOpacity style={styles.stopButton} onPress={stopTracking}>
                <Text style={styles.stopButtonText}>Stop Tracking</Text>
              </TouchableOpacity>
            )}

            {startTime && !isTracking && (
              <View style={styles.completedControls}>
                <Text style={styles.completedText}>
                  Tracking completed in {formatTime(elapsedTime)}
                </Text>
                
                <View style={styles.actionButtons}>
                  <TouchableOpacity style={styles.resetButton} onPress={resetTracking}>
                    <Text style={styles.resetButtonText}>Reset</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity style={styles.saveButton} onPress={saveDuration}>
                    <Text style={styles.saveButtonText}>Save Duration</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>

          {/* Manual Duration Input */}
          <View style={styles.manualInputContainer}>
            <Text style={styles.manualInputLabel}>Or enter duration manually (minutes):</Text>
            <TouchableOpacity 
              style={styles.manualInputButton}
              onPress={() => {
                Alert.prompt(
                  'Enter Duration',
                  'Enter duration in minutes:',
                  [
                    { text: 'Cancel', style: 'cancel' },
                    {
                      text: 'Save',
                      onPress: (text) => {
                        const duration = parseInt(text || '0');
                        if (duration > 0) {
                          onSave(appointment.id, duration);
                          Alert.alert('Success', `Duration saved: ${duration} minutes`);
                        } else {
                          Alert.alert('Error', 'Please enter a valid duration');
                        }
                      },
                    },
                  ],
                  'plain-text',
                  '',
                  'numeric'
                );
              }}
            >
              <Text style={styles.manualInputButtonText}>Enter Manual Duration</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    backgroundColor: '#007bff',
    padding: 20,
    paddingTop: 50,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
  },
  closeButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    width: 30,
    height: 30,
    borderRadius: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  appointmentInfo: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  customerName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 10,
  },
  appointmentDetails: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  timerContainer: {
    backgroundColor: 'white',
    padding: 30,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  timerLabel: {
    fontSize: 16,
    color: '#666',
    marginBottom: 10,
  },
  timerDisplay: {
    fontSize: 48,
    fontWeight: 'bold',
    color: '#007bff',
  },
  controlsContainer: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  startButton: {
    backgroundColor: '#28a745',
    paddingVertical: 15,
    borderRadius: 8,
  },
  startButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  stopButton: {
    backgroundColor: '#dc3545',
    paddingVertical: 15,
    borderRadius: 8,
  },
  stopButtonText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  completedControls: {
    alignItems: 'center',
  },
  completedText: {
    fontSize: 16,
    color: '#28a745',
    fontWeight: 'bold',
    marginBottom: 20,
  },
  actionButtons: {
    flexDirection: 'row',
    width: '100%',
  },
  resetButton: {
    backgroundColor: '#6c757d',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    flex: 1,
    marginRight: 10,
  },
  resetButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  saveButton: {
    backgroundColor: '#007bff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    flex: 1,
    marginLeft: 10,
  },
  saveButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  manualInputContainer: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  manualInputLabel: {
    fontSize: 16,
    color: '#333',
    marginBottom: 15,
  },
  manualInputButton: {
    backgroundColor: '#f8f9fa',
    paddingVertical: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  manualInputButtonText: {
    color: '#007bff',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
});

export default DurationTrackingModal;